import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { PetFormData } from "@/pages/CadastrarPet";
import { Dog, Cat, HelpCircle, Camera } from "lucide-react";

interface Props {
  formData: PetFormData;
  updateField: <K extends keyof PetFormData>(field: K, value: PetFormData[K]) => void;
  idade: string;
  photoPreview: string | null;
  handlePhotoChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

const DadosBasicos = ({
  formData,
  updateField,
  idade,
  photoPreview,
  handlePhotoChange,
}: Props) => {
  return (
    <div className="grid gap-5">
      
      {/* Tipo do Pet */}
      <div>
        <Label className="text-sm font-semibold text-foreground mb-2 block">Tipo do Pet *</Label>
        <div className="grid grid-cols-3 gap-3">
          {[
            { value: "cachorro" as const, label: "Cachorro", icon: Dog },
            { value: "gato" as const, label: "Gato", icon: Cat },
            { value: "outro" as const, label: "Outro", icon: HelpCircle },
          ].map(({ value, label, icon: Icon }) => (
            <button
              key={value}
              type="button"
              onClick={() => updateField("tipo_pet", value)}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-smooth ${
                formData.tipo_pet === value
                  ? "border-primary bg-forest-light text-forest"
                  : "border-border hover:border-primary/40 text-muted-foreground"
              }`}
            >
              <Icon className="w-6 h-6" />
              <span className="text-sm font-semibold">{label}</span>
            </button>
          ))}
        </div>
      </div>

      {formData.tipo_pet === "outro" && (
        <div>
          <Label htmlFor="outro_tipo" className="text-sm font-semibold">
            Qual tipo?
          </Label>
          <Input
            id="outro_tipo"
            value={formData.outro_tipo_pet}
            onChange={(e) => updateField("outro_tipo_pet", e.target.value)}
            placeholder="Ex: Hamster, Coelho..."
            className="mt-1"
          />
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="nome" className="text-sm font-semibold">
            Nome do Pet *
          </Label>
          <Input
            id="nome"
            value={formData.nome}
            onChange={(e) => updateField("nome", e.target.value)}
            placeholder="Como ele(a) se chama?"
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="raca" className="text-sm font-semibold">
            Raça
          </Label>
          <Input
            id="raca"
            value={formData.raca}
            onChange={(e) => updateField("raca", e.target.value)}
            placeholder="Ex: Labrador, SRD..."
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-sm font-semibold">Sexo</Label>
          <Select
            value={formData.sexo_pet}
            onValueChange={(v) => updateField("sexo_pet", v as PetFormData["sexo_pet"])}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Macho">Macho</SelectItem>
              <SelectItem value="Femea">Fêmea</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="data_nascimento" className="text-sm font-semibold">
            Data de Nascimento
          </Label>
          <Input
            id="data_nascimento"
            type="date"
            value={formData.data_nascimento}
            onChange={(e) => updateField("data_nascimento", e.target.value)}
            className="mt-1"
          />
        </div>

        <div>
          <Label htmlFor="peso" className="text-sm font-semibold">
            Peso (kg)
          </Label>
          <Input
            id="peso"
            type="number"
            step="0.1"
            value={formData.peso}
            onChange={(e) => updateField("peso", e.target.value)}
            placeholder="Ex: 12.5"
            className="mt-1"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="text-sm font-semibold">Idade</Label>
          <div className="mt-1 h-10 px-3 flex items-center rounded-md bg-muted text-muted-foreground text-sm">
            {idade || "Informe a data de nascimento"}
          </div>
        </div>

        <div>
          <Label className="text-sm font-semibold">Castrado</Label>
          <Select
            value={formData.castrado ? "1" : "0"}
            onValueChange={(v) => updateField("castrado", v === "1")}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Não</SelectItem>
              <SelectItem value="1">Sim</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-sm font-semibold">Microchipado</Label>
          <Select
            value={formData.microchipado ? "1" : "0"}
            onValueChange={(v) => updateField("microchipado", v === "1")}
          >
            <SelectTrigger className="mt-1">
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Não</SelectItem>
              <SelectItem value="1">Sim</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {formData.microchipado && (
        <div>
          <Label htmlFor="microchip_codigo" className="text-sm font-semibold">
            Código do microchip
          </Label>
          <Input
            id="microchip_codigo"
            value={formData.microchip_codigo}
            onChange={(e) => updateField("microchip_codigo", e.target.value)}
            placeholder="Informe o código do microchip"
            className="mt-1"
          />
        </div>
      )}

      <div>
        <Label htmlFor="observacoes" className="text-sm font-semibold">
          Observações Gerais
        </Label>
        <Textarea
          id="observacoes"
          value={formData.observacoes}
          onChange={(e) => updateField("observacoes", e.target.value)}
          placeholder="Alergias, rotina, comportamento ou observações importantes"
          rows={4}
          className="mt-1"
        />
      </div>
    </div>
  );
};

export default DadosBasicos;